import React, { useState, useMemo, useEffect } from 'react';
import {
  DataType as CapabilityDataType,
  Operation as CapabilityOperation,
} from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/capability.pb';
import { AppointmentTypesApi, AppointmentTypesTypes } from '@frontend/api-appointment-types';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { noApptTypes } from '@frontend/assets';
import { useTranslation, Trans } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { SettingsSection } from '@frontend/settings-ui';
import { theme } from '@frontend/theme';
import {
  useModalControl,
  ConfirmationModal,
  Heading,
  Text,
  useAlert,
  ContentLoader,
  Button,
} from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { AppointmentTypeModal } from '../../components/AppointmentTypeModal';
import { AppointmentTypePaymentsSettings } from '../../components/AppointmentTypePaymentsSettings';
import { ApptTypeSettings } from '../../components/AppointmentTypeSettings/ApptTypeSettings';
import { AppointmentTypeTable } from '../../components/AppointmentTypeTable';
import { useAppointmentTypesCustomizationAndFeatureFlagsHelper } from '../../hooks/use-appointment-types-customization-and-feature-flags-helper';
import { useCheckPaymentsEnabled } from '../../hooks/use-check-payments-enabled';
import * as styles from './styles';

type AppointmentTypesProps = {
  locationId?: string;
};

export function AppointmentTypes({ locationId }: AppointmentTypesProps) {
  const { t } = useTranslation('schedule');
  const alert = useAlert();
  const { selectedLocationIds, isSingleTypeScope } = useAppScopeStore();
  const [reqNumInputValue, setReqNumInputValue] = useState<number>();

  const selectedLocationId =
    selectedLocationIds.length === 1 ? selectedLocationIds[0] : locationId || selectedLocationIds[0] || '';

  const { hasListAppointmentTypesCapability, isOnlineSchedulingEnabled, isLoading } =
    useAppointmentTypesCustomizationAndFeatureFlagsHelper(selectedLocationId);

  const { hasIntegrationCapability: hasCreateAppointmentCapability, isLoading: isLoadingCreateAppointmentCapability } =
    SchedulerV3.Helpers.useCheckIntegrationCapability(
      { type: CapabilityDataType.APPOINTMENT, operation: CapabilityOperation.CREATE },
      selectedLocationId
    );

  const { isPaymentsEnabled } = useCheckPaymentsEnabled(selectedLocationId);

  const [selectedAppointmentTypeId, setSelectedAppointmentTypeId] = useState<string | undefined>();

  const { data: externalAppointmentTypesData, isLoading: isLoadingExternalAppointmentTypes } = useQuery({
    queryKey: ['externalAppointmentTypes', selectedLocationId],
    queryFn: () => AppointmentTypesApi.getExternalAppointmentTypes(selectedLocationId),
    enabled: !!hasListAppointmentTypesCapability,
    onSettled: () => SchedulerV3.trackScheduleAPIVersionChange('v2', { endpoint: 'getExternalAppointmentTypes' }),
  });

  const {
    data: appointmentTypesData,
    refetch: refetchAppointmentTypeData,
    isLoading: isLoadingAppointmentTypes,
    isFetching: isFetchingAppointmentTypes,
  } = useQuery({
    queryKey: ['appointmentTypes', selectedLocationId],
    queryFn: () => {
      return AppointmentTypesApi.getAppointmentTypes(selectedLocationId);
    },
    enabled: !!selectedLocationId,
    onSettled: () => SchedulerV3.trackScheduleAPIVersionChange('v2', { endpoint: 'getAppointmentTypes' }),
  });
  const selectedAppointmentType = useMemo(() => {
    if (!selectedAppointmentTypeId || !appointmentTypesData) {
      return {} as Partial<AppointmentTypesTypes.AppointmentType>;
    }
    return appointmentTypesData.find(({ id }) => id === selectedAppointmentTypeId);
  }, [selectedAppointmentTypeId, appointmentTypesData]);

  const { modalProps: editModalProps, triggerProps: editTriggerProps } = useModalControl();

  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();

  const handleAddNew = () => {
    setSelectedAppointmentTypeId(undefined);
    editTriggerProps.onClick();
  };

  const handleDelete = (appointmentTypeId?: string, e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e?.stopPropagation();
    setSelectedAppointmentTypeId(appointmentTypeId);
    deleteTriggerProps.onClick();
  };

  const handleConfirmDelete = async () => {
    if (!selectedAppointmentTypeId) {
      return;
    }
    try {
      await AppointmentTypesApi.deleteAppointmentType(selectedAppointmentTypeId);
      alert.success(t('Successfully deleted appointment type'));
    } catch (error: any) {
      console.error(error);
      alert.error(t('Failed to delete appointment type'));
    } finally {
      refetchAppointmentTypeData();
      setSelectedAppointmentTypeId(undefined);
    }
  };

  const handleEdit = (appointmentTypeId?: string, e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e?.stopPropagation();
    setSelectedAppointmentTypeId(appointmentTypeId);
    editTriggerProps.onClick();
  };

  const {
    isLoading: apptReqConfigIsLoading,
    isError: apptReqConfigFetchError,
    data: apptRequestConfig,
    refetch: refetchApptReqConfig,
  } = useQuery({
    queryKey: queryKeys.apptReqConfig(selectedLocationId),
    queryFn: () => AppointmentTypesApi.getApptRequestConfig(selectedLocationId),
    retry: 1,
  });

  const updateLeadTime = async (requestBufferDuration: number) => {
    try {
      await AppointmentTypesApi.updateApptRequestConfig(
        locationId ?? '',
        apptRequestConfig?.appointmentTypeIDs || [],
        'requestBufferDuration',
        requestBufferDuration
      );
      alert.success(t('Successfully updated lead time'));
    } catch (error: any) {
      alert.error(t('Failed to update lead time'));
    } finally {
      refetchApptReqConfig();
    }
  };

  const updateMinOpenings = async (minOpeningsPermitted: number) => {
    try {
      await AppointmentTypesApi.updateApptRequestConfig(
        locationId ?? '',
        apptRequestConfig?.appointmentTypeIDs || [],
        'minOpeningsPermitted',
        minOpeningsPermitted
      );
      alert.success(t('Successfully updated required appt. times'));
    } catch (error: any) {
      alert.error(t('Failed to update required appt. times'));
    } finally {
      refetchApptReqConfig();
    }
  };

  useEffect(() => {
    refetchAppointmentTypeData();
  }, []);

  useEffect(() => {
    if (appointmentTypesData) refetchApptReqConfig();
  }, [appointmentTypesData]);

  const shouldShowLocationSelector = selectedLocationIds.length > 1 && !isSingleTypeScope;
  return (
    <SettingsSection css={{ position: 'relative' }}>
      <ContentLoader show={isLoadingAppointmentTypes || isLoading} />
      {!appointmentTypesData?.length ? (
        <>
          <Text css={styles.addAppointmentText} textAlign='center'>
            <Trans t={t}>
              Select
              <strong> Add Appointment Type </strong>
              below to create the appointment types that patients will choose from when making appointment requests
              online.
            </Trans>
          </Text>

          <img alt={t("You haven't set up appointment types yet")} css={styles.noApptTypesImg} src={noApptTypes} />

          <Button size='large' onClick={handleAddNew} css={styles.noApptTypesAddButton}>
            {t('Add Appointment Type')}
          </Button>
        </>
      ) : (
        <>
          {isOnlineSchedulingEnabled && (
            <>
              <Heading level={2} textAlign='left'>
                {t('Online Scheduling Appointment Settings')}
              </Heading>

              <ApptTypeSettings
                numOfApptTypes={appointmentTypesData?.length}
                apptRequestConfig={apptRequestConfig}
                updateLeadTime={updateLeadTime}
                updateMinOpenings={updateMinOpenings}
                apptReqConfigIsLoading={apptReqConfigIsLoading}
                apptReqConfigFetchError={apptReqConfigFetchError}
                setReqNumInputValue={setReqNumInputValue}
              />
              {isPaymentsEnabled && !apptReqConfigIsLoading && (
                <section css={{ marginTop: theme.spacing(3) }}>
                  <Heading level={2} textAlign='left'>
                    {t('Appointment Type Payments Settings')}
                  </Heading>

                  <AppointmentTypePaymentsSettings
                    apptRequestConfig={apptRequestConfig}
                    refetchApptReqConfig={refetchAppointmentTypeData}
                    locationId={selectedLocationId}
                  />
                </section>
              )}
            </>
          )}

          <section
            css={styles.tableHeader({
              shouldHaveMarginTop: isOnlineSchedulingEnabled,
            })}
          >
            <Heading level={2} textAlign='left'>
              {t('Appointment Types')}
            </Heading>
            <Text textAlign='left' css={styles.maxWidth}>
              {t(
                'Create the appointment types that patients will be able to choose from when scheduling online. (Ex: New Customer Consultation)'
              )}
            </Text>
          </section>

          <Button size='large' {...editTriggerProps} onClick={handleAddNew} css={styles.primaryButton}>
            {t('Add New')}
          </Button>

          <AppointmentTypeTable
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            appointmentTypesData={appointmentTypesData}
            externalAppointmentTypes={externalAppointmentTypesData}
            showAmountColumn={isPaymentsEnabled && isOnlineSchedulingEnabled}
            isLoading={
              isLoadingAppointmentTypes ||
              isFetchingAppointmentTypes ||
              isLoadingExternalAppointmentTypes ||
              isLoadingCreateAppointmentCapability
            }
          />
        </>
      )}

      <AppointmentTypeModal
        {...editModalProps}
        refetch={refetchAppointmentTypeData}
        locationId={selectedLocationId}
        appointmentType={selectedAppointmentType as AppointmentTypesTypes.AppointmentType}
        externalAppointmentTypes={externalAppointmentTypesData as AppointmentTypesTypes.ExternalAppointmentType[]}
        shouldShowAppointmentTypeLabelColors={false} // TODO: Pass the prop as true when we have plan to show case the label colors as per the direction from the product team
        hasCreateAppointmentCapability={!!hasCreateAppointmentCapability}
        showLocationSelector={!!shouldShowLocationSelector}
        maxOpeningsPermitted={apptRequestConfig?.minOpeningsPermitted || reqNumInputValue}
        globalBookingDepositAmount={apptRequestConfig?.globalBookingDepositAmount}
        hasGlobalBookingDeposit={apptRequestConfig?.hasGlobalBookingDeposit}
        isPaymentsEnabled={isPaymentsEnabled && isOnlineSchedulingEnabled}
      />

      <ConfirmationModal
        {...deleteModalProps}
        title={t('Delete Appointment Type')}
        destructive
        message={t('Are you sure you want to delete {{name}}?', {
          name: selectedAppointmentType?.name,
        })}
        confirmLabel={t('Delete')}
        cancelLabel={t('Cancel')}
        onCancel={deleteModalProps.onClose}
        onConfirm={handleConfirmDelete}
      />
    </SettingsSection>
  );
}
